import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import type { ReactNode } from "react";
import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from "aws-amplify/auth";
import { config } from "../config";

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.clientId,
      loginWith: {
        oauth: {
          domain: config.cognito.domain,
          scopes: ["email", "openid", "profile"],
          redirectSignIn: [`${window.location.origin}/callback`],
          redirectSignOut: [`${window.location.origin}/`],
          responseType: "code",
        },
      },
    },
  },
});

// Types
export interface User {
  id: string;
  email: string;
  name?: string;
  emailVerified: boolean;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name?: string,
  ) => Promise<{ isSignUpComplete: boolean; userId?: string }>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendSignUpCode: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<void>;
  refreshSession: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  getAccessToken: () => Promise<string | null>;
  clearError: () => void;
}

// Create context with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Helper to update state
  const updateState = useCallback((updates: Partial<AuthState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  // Fetch user attributes and build user object
  const fetchUser = useCallback(async (): Promise<User | null> => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      return {
        id: currentUser.userId,
        email: attributes.email || "",
        name: attributes.name,
        emailVerified: attributes.email_verified === "true",
      };
    } catch {
      return null;
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await fetchUser();
        updateState({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        });
      } catch {
        updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    checkAuth();
  }, [fetchUser, updateState]);

  // Sign in with email and password
  const signIn = useCallback(
    async (email: string, password: string) => {
      updateState({ isLoading: true, error: null });

      try {
        const input: SignInInput = {
          username: email,
          password,
        };

        const result = await amplifySignIn(input);

        if (result.isSignedIn) {
          const user = await fetchUser();
          updateState({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          // Handle additional sign-in steps if needed (MFA, etc.)
          updateState({ isLoading: false });
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Sign in failed";
        updateState({
          error: message,
          isLoading: false,
        });
        throw error;
      }
    },
    [fetchUser, updateState],
  );

  // Sign up with email and password
  const signUp = useCallback(
    async (email: string, password: string, name?: string) => {
      updateState({ isLoading: true, error: null });

      try {
        const input: SignUpInput = {
          username: email,
          password,
          options: {
            userAttributes: {
              email,
              ...(name && { name }),
            },
          },
        };

        const result = await amplifySignUp(input);
        updateState({ isLoading: false });

        return {
          isSignUpComplete: result.isSignUpComplete,
          userId: result.userId,
        };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Sign up failed";
        updateState({
          error: message,
          isLoading: false,
        });
        throw error;
      }
    },
    [updateState],
  );

  // Confirm sign up with verification code
  const confirmSignUp = useCallback(
    async (email: string, code: string) => {
      updateState({ isLoading: true, error: null });

      try {
        const input: ConfirmSignUpInput = {
          username: email,
          confirmationCode: code,
        };

        await amplifyConfirmSignUp(input);
        updateState({ isLoading: false });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Confirmation failed";
        updateState({
          error: message,
          isLoading: false,
        });
        throw error;
      }
    },
    [updateState],
  );

  // Resend sign up verification code
  const resendSignUpCode = useCallback(
    async (email: string) => {
      updateState({ isLoading: true, error: null });

      try {
        await amplifyResendSignUpCode({ username: email });
        updateState({ isLoading: false });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to resend code";
        updateState({
          error: message,
          isLoading: false,
        });
        throw error;
      }
    },
    [updateState],
  );

  // Sign out
  const signOut = useCallback(async () => {
    updateState({ isLoading: true, error: null });

    try {
      await amplifySignOut();
      updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Sign out failed";
      updateState({
        error: message,
        isLoading: false,
      });
      throw error;
    }
  }, [updateState]);

  // Initiate password reset
  const resetPassword = useCallback(
    async (email: string) => {
      updateState({ isLoading: true, error: null });

      try {
        const input: ResetPasswordInput = {
          username: email,
        };

        await amplifyResetPassword(input);
        updateState({ isLoading: false });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Password reset failed";
        updateState({
          error: message,
          isLoading: false,
        });
        throw error;
      }
    },
    [updateState],
  );

  // Confirm password reset with code and new password
  const confirmResetPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      updateState({ isLoading: true, error: null });

      try {
        const input: ConfirmResetPasswordInput = {
          username: email,
          confirmationCode: code,
          newPassword,
        };

        await amplifyConfirmResetPassword(input);
        updateState({ isLoading: false });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Password reset confirmation failed";
        updateState({
          error: message,
          isLoading: false,
        });
        throw error;
      }
    },
    [updateState],
  );

  // Refresh the current session
  const refreshSession = useCallback(async () => {
    try {
      const user = await fetchUser();
      updateState({
        user,
        isAuthenticated: !!user,
      });
    } catch (error) {
      updateState({
        user: null,
        isAuthenticated: false,
      });
    }
  }, [fetchUser, updateState]);

  // Get ID token for API calls (contains email/name claims)
  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(
    () => ({
      ...state,
      signIn,
      signUp,
      signOut,
      confirmSignUp,
      resendSignUpCode,
      resetPassword,
      confirmResetPassword,
      refreshSession,
      getIdToken,
      getAccessToken,
      clearError,
    }),
    [
      state,
      signIn,
      signUp,
      signOut,
      confirmSignUp,
      resendSignUpCode,
      resetPassword,
      confirmResetPassword,
      refreshSession,
      getIdToken,
      getAccessToken,
      clearError,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

// Export context for advanced use cases
export { AuthContext };
